import { Property } from 'csstype'
import * as React from 'react'
import { isFunction } from '@reach/utils'
import { usePrevious } from 'react-use'

import CardContainer from 'components/common/CardContainer'
import Flex from 'components/common/Flex'
import theme from 'theme'

type DashboardCardProps = React.HTMLAttributes<HTMLElement> & {
  headerContent: ({
    selectedFilter,
    setSelectedFilter
  }: {
    selectedFilter: string
    setSelectedFilter(value: string): void
  }) => React.ReactNode
  filters: { value: string; label: string }[]
  showHoverEffect?: boolean
  defaultFilter?: string
  height?: number
  overflow?: Property.Overflow
  scrollableDivRef?: React.RefObject<HTMLDivElement>
  children:
    | (({ selectedFilter }: { selectedFilter: string }) => React.ReactNode)
    | React.ReactNode
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  headerContent,
  showHoverEffect = true,
  defaultFilter,
  children,
  height = theme.space[9],
  overflow = 'scroll',
  filters,
  scrollableDivRef,
  ...rest
}) => {
  const [selectedFilter, setSelectedFilter] = React.useState<
    string | undefined
  >(defaultFilter ?? filters?.[0]?.value)
  const prevDefaultFilter = usePrevious(defaultFilter)

  React.useEffect(() => {
    if (
      prevDefaultFilter !== defaultFilter &&
      selectedFilter !== defaultFilter
    ) {
      setSelectedFilter(defaultFilter)
    }
  }, [defaultFilter, prevDefaultFilter, selectedFilter])

  return (
    <CardContainer
      css={{ borderRadius: theme.borderRadius[0], padding: 0 }}
      data-testid="dashboard-card"
      showHoverEffect={showHoverEffect}
      {...rest}
    >
      <Flex column>
        {headerContent ? (
          <div
            css={{
              backgroundColor: theme.colors.gray[0],
              borderBottom: `1px solid ${theme.colors.border[0]}`,
              borderTopLeftRadius: theme.borderRadius[0],
              borderTopRightRadius: theme.borderRadius[0]
            }}
            data-testid="dashboard-card-header"
          >
            {typeof headerContent === 'function'
              ? headerContent({
                  setSelectedFilter,
                  selectedFilter: selectedFilter
                })
              : headerContent}
          </div>
        ) : null}
        <div
          ref={scrollableDivRef}
          css={{
            height: height,
            overflow: overflow,
            backgroundColor: theme.colors.white[0],
            borderBottomLeftRadius: theme.borderRadius[0],
            borderBottomRightRadius: theme.borderRadius[0]
          }}
        >
          {isFunction(children) ? children({ selectedFilter }) : children}
        </div>
      </Flex>
    </CardContainer>
  )
}
export default DashboardCard
